"use client"

import { useForm, SubmitHandler } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { useToast } from "@/components/ui/use-toast"
import { PlusCircle, Loader2 } from "lucide-react"
import { supabase } from "@/utils/supabase/client"
import { ExpenseCategory } from "@/types"
import { useEffect, useState } from "react"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Controller } from "react-hook-form"
import { useAuth } from "@/contexts/auth-context"

const expenseSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters."),
  amount: z.coerce.number().positive("Amount must be a positive number."),
  expense_date: z.string().nonempty("Date is required."),
  category_id: z.string().nonempty("Category is required."),
})

type ExpenseFormData = z.infer<typeof expenseSchema>

interface NewExpenseFormProps {
  onExpenseAdded: () => void;
}

export function NewExpenseForm({ onExpenseAdded }: NewExpenseFormProps) {
    const { toast } = useToast()
    const { currentOrganization } = useAuth()
  const [isSubmitting, setIsSubmitting] = useState(false)
    const [categories, setCategories] = useState<ExpenseCategory[]>([])
  const { register, handleSubmit, reset, control, formState: { errors } } = useForm<ExpenseFormData>({
        resolver: zodResolver(expenseSchema),
        defaultValues: {
      expense_date: new Date().toISOString().split('T')[0]
        }
    })

    useEffect(() => {
        const fetchCategories = async () => {
      if(!currentOrganization) return;
      const { data } = await supabase
        .from('expense_categories')
        .select('*')
        .eq('organization_id', currentOrganization.id);

      if(data) setCategories(data);
    }
    fetchCategories();
  }, [currentOrganization])

    const handleFormSubmit: SubmitHandler<ExpenseFormData> = async (data) => {
      if(!currentOrganization) {
        toast({ title: "Error", description: "No organization selected.", variant: "destructive" })
        return;
      }
        
      console.log("Submitting form with data:", data);
      console.log("Using organization ID:", currentOrganization.id);

        setIsSubmitting(true)
        try {
      const expensePayload = {
        ...data,
        organization_id: currentOrganization.id
      };

      console.log("Payload sent to Supabase:", expensePayload);

      const { data: insertedData, error } = await supabase.from("expenses").insert([expensePayload]).select();
      
      console.log("Supabase response - data:", insertedData);
      console.log("Supabase response - error:", error);

      if (error) {
        console.error("Error inserting expense:", error);
        throw error;
      }

      toast({ title: "Expense created", description: "Your new expense has been recorded." })
      onExpenseAdded();
            reset({
        name: '',
        amount: 0,
        expense_date: new Date().toISOString().split('T')[0],
        category_id: data.category_id, 
      })
    } catch (error: any) {
      console.error("Caught error in handleFormSubmit:", error);
      toast({ title: "Error", description: `Could not create the expense. ${error.message}`, variant: "destructive" })
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
    <Card className="max-w-2xl mx-auto mt-6">
      <CardHeader>
        <CardTitle>Record New Expense</CardTitle>
        <CardDescription>Fill in the details of the expense to record it.</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
                            <div>
            <label htmlFor="name">Description</label>
            <Input id="name" {...register("name")} />
            {errors.name && <p className="text-red-500 text-sm">{errors.name.message}</p>}
                            </div>
                                <div>
                                    <label htmlFor="amount">Amount</label>
            <Input id="amount" type="number" step="0.01" {...register("amount")} />
            {errors.amount && <p className="text-red-500 text-sm">{errors.amount.message}</p>}
                                </div>
                                <div>
                                    <label htmlFor="category_id">Category</label>
            <Controller
              name="category_id"
              control={control}
              render={({ field }) => (
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                                        <SelectContent>
                                            {categories.map(cat => <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
              )}
            />
            {errors.category_id && <p className="text-red-500 text-sm">{errors.category_id.message}</p>}
                            </div>
                             <div>
            <label htmlFor="expense_date">Expense Date</label>
            <Input id="expense_date" type="date" {...register("expense_date")} />
            {errors.expense_date && <p className="text-red-500 text-sm">{errors.expense_date.message}</p>}
                            </div>
          <div className="flex justify-end">
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <PlusCircle className="mr-2 h-4 w-4" />
              )}
              Record Expense
                    </Button>
            </div>
        </form>
      </CardContent>
    </Card>
    )
} 